import { useMemo } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import * as api from "lib/utils/apiHelpers"
import _ from 'lodash';

export interface IQueryArgs {
  qtype:string
  resource:string 
  modifier?:string    // deprecated
  qkeys:string[]
  enabled?:boolean
  data?:any 
  id?:string|null|undefined
  _id?:string|null|undefined
  params?:{
    _page?:number
    _perpage?:number
    _sortby?:string
    _sortdir?:string
    [key:string]:any
  }
  config?:{
    prefix?:string
    path?:string
    method?:string,
    onSuccess?:(result:any) => any
    onError?:(result:any) => any
  }
}

const convertParamsToModifier = (params:any) => {
  let arr:any[] = []
  if( params ){
    Object.keys(params).filter((key:string) => key !== "querytype" && key !== "resource").forEach( (key:string) => {
      const val = `${key}:${params[key]}`
      arr = [...arr, val]
    })
  }
  const result = arr.join(".")
  console.log(`
    These params: ${JSON.stringify(params)}
    were converted to this modifier: ${result}
  `)
  return result
}

/** the unique query key string is always divided into 3 segments:
 * 1. resource name (e.g. "content")
 * 2. query type (e.g. "all", "one", "single")
 * 3. modifiers OR unique id (e.g. "snippets", "published", etc.) 
 * 
 *  for collection queries (findAll):
 *    resource.all.[queryparams]
 *  for single (findById) queries:
 *    resource.one.[id]
 *  for single (findOne) queries:
 *    resource.one.[modifiers]
 */

const getQtype = (method:any) => {
  if( method === "findAll" ){
    return "all"
  } else if (method === "findOne" ){
    return "one"
  } else {
    return "id"
  }
}

/* 
  final key should look like this:
  [ "<resource>.<method>", "<resource>.<method>.[id].[filters].[displayParams]" ]
   filters should be:  <key>:value
   displayParams should be: <key>:value
*/
const generateQueryKey = (resource:string, method:string, displayParams:any, filters:any ) => {
  let querykey = [resource, method]
  
  const qtype = getQtype(method)
  const displays = generateDisplayParams(displayParams, {})

  Object.entries(displays).forEach(([key, value]) => {
    querykey = [...querykey, `${key}:${value}`]
  })

  Object.entries(filters).forEach(([key, value]) => {
    querykey = [...querykey, `${key}:${value}`]
  })

  return querykey

  // const filters = generateDisplayParams(filter, {})

  console.log(filters)
  let modifierStr = filters.length || displays.length ? `${filters}.${displays}` : ""

  // let key = ( qtype === "all" ) 
  //   ? [resource, qtype, convertParamsToModifier(args)].join(".") 
  //   : [resource, qtype, identifier].join(".")

  if(modifierStr.endsWith(".")){
    modifierStr = modifierStr.slice(0, -1)
  }

  return modifierStr.length ? [`${resource}`, modifierStr] : [`${resource}`]
}


const generateDisplayParams = (args:any, config:any) => {
  if( !config?.paginate || config.paginate === false ) return ""
  const allowedKeys = ["_page", "_perpage", "_sortby", "_sortdir"]
  let queryObj = {}
  let queryStr = ""
  Object.keys(args).filter((key:string) => allowedKeys.includes(key)).forEach( (key:string) => {
    queryObj = {...queryObj, [key]: args[key]}
    queryStr += `${key}:${args[key]}.`
  })
  return queryStr.slice(0, -1)
}

const generateFilters = (args:any) => {
  let filters = {}
  Object.keys(args).filter((key:string) => key.startsWith("_") === false && key !== "data").forEach( (key:string) => {
    filters = {...filters, [key]: args[key] === null ? "null" : args[key]}
  })
  // console.log("generateFilters", filters)
  return filters
}

const generateIdObject = (args:any) => {
  if (!args.id && !args._id && !args.data?.id && !args.data?._id) return null
  let key 
  let value
  if( args.id || args._id ){
    key = args.id ? "id" : "_id"
    value = args[key]
  } else {
    key = args.data.id ? "id" : "_id"
    value = args.data[key]
  }
  // console.log("generateIdObject", key, value)
  return { key, value }
}


/** this is the new version that should be called going forward
 * *  @param naming - the resource name and method (e.g. "content.findAll")
 *    @param all arguments that will be part of the query (e.g. { _id: "123", _page: 1, _perpage: 10 })
 *    @param config - any additional configuration for the query (e.g. { enabled: true, paginate: true })
 * *  @returns a useQuery object that can be used in a component
 */
const parseQueryForApi = (naming:string, args:any, config?:any) => {
  const apiObj:any = api
  const resource = naming.split(".")[0]
  const method = naming.split(".")[1]
  const displayParams = generateDisplayParams(args, config)
  const filters = generateFilters(args)
  const mergedConfig = { enabled: config?.enabled === undefined ? true : config.enabled, ...config}
  return useQuery({
    queryKey: useMemo(
      () => generateQueryKey(resource, method, displayParams, filters),
      [resource, method, JSON.stringify(displayParams), JSON.stringify(filters)]
    ),
    queryFn: () => {
      const payload:any = apiObj[method]({ 
        resource,
        idObj: generateIdObject(args),
        displayParams,
        filters,
        data: args.data || null,
        config: { onSuccess: mergedConfig.onSuccess || null, onError: mergedConfig.onError || null },
        // _page: mergedConfig.paginate ? args._page || 1 : 1,
        // _perpage: mergedConfig.paginate ? args._perpage || 25 : 9999,
        // _sortby: mergedConfig.paginate ? args._sortby || undefined : "createdAt",
        // _sortdir: mergedConfig.paginate ? args._sortdir || undefined : "asc",
        // _paginate: mergedConfig.paginate || "false",
        // config: mergedConfig || null
      })
      return payload
    },
    enabled: !!mergedConfig.enabled,
    staleTime: 5000
  })
}



// const getCollection = (args:any) => parseQueryForApi({...args, querytype: "findAll"})
// const getOne = (args:any) => parseQueryForApi({...args, querytype: "findOne"})
// const getById = (args:any) => parseQueryForApi({...args, querytype: "findById"})

const useSaveResource = (invalidate:string[] = [], callbackFn?:any) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args:any) => {
      console.log("mutating now...")
      const result = api.save({ 
        resource: args.resource, 
        idObj: { key: "_id", value: args.data?._id || undefined }, 
        data: args.data 
      })
      return result
    },
    onSuccess: (data, args) => {
      console.log("success", invalidate)
      queryClient.invalidateQueries({queryKey: invalidate});
      // queryClient.setQueryData(['template.one'], data);
      if( callbackFn ) {
        console.log("callbackFn", data)
        callbackFn(data)
      }
      return data

    }
  })
}
const useDeleteResource = (invalidate:string[] = [], callbackFn?:any) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ resource, idObj }:{ resource: string; idObj: {key:string, value: string} }) => {
      const result = api.remove({ resource: resource, idObj })
      return result
    },
    onSuccess: (data, args) => {
      queryClient.invalidateQueries({queryKey: invalidate})
      if( callbackFn ) {
        callbackFn(data)
      }
      return data
    }
  })
}

export const queryTools = {
  resource: {
    parseQueryForApi,
    save: useSaveResource,
    remove: useDeleteResource
  }
}